import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const STORAGE_KEY = 'blazepos-feature-flags';
const FONT_STORAGE_KEY = 'blazepos-system-font';

export interface FeatureFlags {
  showClassicDashboardCards: boolean;
  showGlobalViewNotice: boolean;
  autoCollapseSidebarOnReports: boolean;
  autoCollapseSidebarOnInventory: boolean;
  autoClearPosOrderOnLeave: boolean;
  expandedUpsellPopover: boolean;
  productViewFullPanel: boolean;
  vibrantAvatars: boolean;
  showTeamSettings: boolean;
  showLocationSettings: boolean;
  showPaymentSettings: boolean;
  showSidebarSectionHeaders: boolean;
  showFloorPlan: boolean;
  useAlternativeLogin: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  showClassicDashboardCards: false,
  showGlobalViewNotice: false,
  autoCollapseSidebarOnReports: false,
  autoCollapseSidebarOnInventory: true,
  autoClearPosOrderOnLeave: false,
  expandedUpsellPopover: false, // Compact by default, toggle on for larger display
  productViewFullPanel: false, // Inline expansion by default, toggle on for slide-out panel
  vibrantAvatars: false, // Use role-based pastel colors by default, toggle for vibrant per-user colors
  showTeamSettings: false, // Default off, moved to Command Center
  showLocationSettings: false, // Default off, moved to Command Center
  showPaymentSettings: false, // Default off, moved to Command Center
  showSidebarSectionHeaders: true, // Show section headers in sidebar by default
  showFloorPlan: false, // Floor plan feature is off by default
  useAlternativeLogin: false, // Use modern split-screen login design
};

interface UserSettings {
  feature_flags?: Partial<FeatureFlags>;
  appearance?: {
    theme?: 'light' | 'dark' | 'system';
  };
}

export type SystemFont = 'inter' | 'system' | 'roboto' | 'sf-pro';

export const SYSTEM_FONTS: { value: SystemFont; label: string; fontFamily: string }[] = [
  { value: 'inter', label: 'Inter (Default)', fontFamily: "'Inter', system-ui, sans-serif" },
  { value: 'system', label: 'System Default', fontFamily: 'system-ui, -apple-system, sans-serif' },
  { value: 'roboto', label: 'Roboto', fontFamily: "'Roboto', system-ui, sans-serif" },
  { value: 'sf-pro', label: 'SF Pro', fontFamily: "'SF Pro Display', system-ui, sans-serif" },
];

export function useFeatureFlags() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const dbFlagsApplied = useRef(false);

  const [flags, setFlagsState] = useState<FeatureFlags>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_FLAGS, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.error('Failed to load feature flags:', e);
    }
    return DEFAULT_FLAGS;
  });

  const [systemFont, setSystemFontState] = useState<SystemFont>(() => {
    try {
      const stored = localStorage.getItem(FONT_STORAGE_KEY);
      if (stored && SYSTEM_FONTS.some(f => f.value === stored)) {
        return stored as SystemFont;
      }
    } catch (e) {
      console.error('Failed to load system font:', e);
    }
    return 'inter';
  });

  // Fetch user settings from database (includes feature_flags)
  const { data: userSettings } = useQuery({
    queryKey: ['user-settings', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return (data?.settings as UserSettings) || {};
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Apply database flags when they load (merge with localStorage, DB takes precedence)
  useEffect(() => {
    if (userSettings?.feature_flags && !dbFlagsApplied.current) {
      const mergedFlags = { ...DEFAULT_FLAGS, ...flags, ...userSettings.feature_flags };
      setFlagsState(mergedFlags);
      // Also update localStorage to keep in sync
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedFlags));
      } catch (e) {
        console.error('Failed to sync feature flags to localStorage:', e);
      }
      dbFlagsApplied.current = true;
    }
  }, [userSettings?.feature_flags]);

  // Mutation to save flags to database
  const saveFlagsToDb = useMutation({
    mutationFn: async (newFlags: FeatureFlags) => {
      if (!user?.id) return;

      // First get current settings to preserve other data
      const { data: currentData } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();

      const currentSettings = (currentData?.settings as UserSettings) || {};
      const newSettings: UserSettings = {
        ...currentSettings,
        feature_flags: newFlags,
      };

      const { error } = await supabase
        .from('profiles')
        .update({ settings: newSettings })
        .eq('id', user.id);

      if (error) throw error;
      return newFlags;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings', user?.id] });
    },
  });

  // Apply font to document
  useEffect(() => {
    const fontConfig = SYSTEM_FONTS.find(f => f.value === systemFont);
    if (fontConfig) {
      document.documentElement.style.setProperty('--system-font', fontConfig.fontFamily);
    }
  }, [systemFont]);

  const setFlag = useCallback((key: keyof FeatureFlags, value: boolean) => {
    setFlagsState(prev => {
      const newFlags = { ...prev, [key]: value };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newFlags));
      } catch (e) {
        console.error('Failed to save feature flags:', e);
      }
      // Sync to database if user is authenticated
      if (user?.id) {
        saveFlagsToDb.mutate(newFlags);
      }
      return newFlags;
    });
  }, [user?.id, saveFlagsToDb]);

  const setSystemFont = useCallback((font: SystemFont) => {
    setSystemFontState(font);
    try {
      localStorage.setItem(FONT_STORAGE_KEY, font);
    } catch (e) {
      console.error('Failed to save system font:', e);
    }
  }, []);

  const resetFlags = useCallback(() => {
    setFlagsState(DEFAULT_FLAGS);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to reset feature flags:', e);
    }
    // Also reset in database
    if (user?.id) {
      saveFlagsToDb.mutate(DEFAULT_FLAGS);
    }
  }, [user?.id, saveFlagsToDb]);

  return {
    flags,
    setFlag,
    systemFont,
    setSystemFont,
    resetFlags,
  };
}
